import { clsx } from "@trydig/ui";
import { Asset } from "@/components/ui/Asset";
import { Typography } from "@/components/ui/Typography";
import type { AssetStoryblok } from "@/generated/sb/sb-types";

export interface FigureProps {
	asset?: AssetStoryblok;
	containerClassName?: string;
	mediaClassName?: string;
	/**
	 * Passed straight to `next/image`'s `sizes`. Default assumes the figure
	 * renders at roughly half the viewport width, which under-requests
	 * resolution — and shows up as blur — whenever `mediaClassName` locks in
	 * an aspect ratio far from the source image's own (e.g. a landscape
	 * upload inside an `aspect-3/4` box: `object-cover` then scales off the
	 * *taller* dimension, needing much more source width than the visible
	 * column width alone would suggest). Override with a larger value (or
	 * plain `"100vw"`) for any usage prone to that mismatch.
	 */
	sizes?: string;
}

export const Figure = ({
	asset,
	containerClassName,
	mediaClassName,
	sizes = "(min-width: 1024px) 50vw, 100vw",
}: FigureProps) => {
	return (
		<figure className={clsx("flex flex-col gap-grid-section-vertical-sm", containerClassName)}>
			<div className={clsx("relative w-full overflow-hidden", mediaClassName)}>
				<Asset asset={asset} sizes={sizes} />
			</div>
			{asset?.title && (
				<figcaption>
					<Typography variant="body-xs-reg" className="text-text-dark-text-2">
						{asset.title}
					</Typography>
				</figcaption>
			)}
		</figure>
	);
};
